import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../utils/supabase';
import ReflectionTextContent from '../ReflectionTextContent';

function formatReflectionDate(date) {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(date));
}

function groupReflectionsByLesson(rows, lessonsById) {
  const lessonGroups = new Map();

  (rows || []).forEach((row) => {
    const lessonId = row?.reflection_questions?.questions?.lesson_id;
    const lessonKey = lessonId != null ? String(lessonId) : 'unknown';
    const lessonMeta = lessonsById.get(lessonKey) || null;
    const questionId = String(row.reflection_question_id || row?.reflection_questions?.question_id || row.id);
    const prompt = row?.reflection_questions?.prompt_text || 'Untitled question';

    if (!lessonGroups.has(lessonKey)) {
      lessonGroups.set(lessonKey, {
        lesson: lessonMeta,
        position: Number(lessonMeta?.position || 9999),
        questions: new Map(),
      });
    }

    const lessonGroup = lessonGroups.get(lessonKey);
    if (!lessonGroup.questions.has(questionId)) {
      lessonGroup.questions.set(questionId, { prompt, responses: [] });
    }
    lessonGroup.questions.get(questionId).responses.push(row);
  });

  return Array.from(lessonGroups.values())
    .sort((a, b) => a.position - b.position)
    .map((group) => ({
      ...group,
      questions: Array.from(group.questions.entries())
        .map(([questionId, question]) => ({
          questionId,
          ...question,
          responses: [...question.responses].sort(
            (a, b) => new Date(b.submitted_at).getTime() - new Date(a.submitted_at).getTime(),
          ),
        })),
      responseCount: Array.from(group.questions.values()).reduce(
        (sum, question) => sum + question.responses.length,
        0,
      ),
    }));
}

function UserReflectionResponse({ reflection }) {
  const category = (reflection?.category || '').trim();
  const showCategory = category && category !== 'Uncategorized';

  return (
    <div className="user-reflections-response">
      <div className="user-reflections-response-header">
        <span className="user-reflections-response-label">Response</span>
        <div className="user-reflections-response-meta">
          {showCategory && (
            <span className="user-reflections-category-tag">{category}</span>
          )}
          <time className="user-reflections-response-date" dateTime={reflection.submitted_at}>
            {formatReflectionDate(reflection.submitted_at)}
          </time>
        </div>
      </div>
      <ReflectionTextContent
        reflection={reflection}
        primaryClassName="user-reflections-response-text"
      />
    </div>
  );
}

function BootcampUserReflectionsOverview({ bootcampId, userId, lessons = [] }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const lessonsById = useMemo(() => {
    const map = new Map();
    (lessons || []).forEach((lesson) => {
      map.set(String(lesson.lesson_id), lesson);
    });
    return map;
  }, [lessons]);

  useEffect(() => {
    let mounted = true;

    const loadReflections = async () => {
      if (!bootcampId || !userId) {
        setRows([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');

      const { data, error: fetchError } = await supabase
        .from('user_reflections')
        .select(`
          id,
          reflection_question_id,
          reflection_text,
          follow_up_question,
          follow_up_answer,
          category,
          submitted_at,
          reflection_questions:reflection_questions!inner(
            question_id,
            prompt_text,
            questions:questions!inner(lesson_id)
          )
        `)
        .eq('bootcamp_id', Number(bootcampId))
        .eq('user_id', userId)
        .order('submitted_at', { ascending: false });

      if (!mounted) return;

      if (fetchError) {
        setError('Could not load reflections.');
        setRows([]);
      } else {
        setRows(data || []);
      }
      setLoading(false);
    };

    loadReflections();
    return () => { mounted = false; };
  }, [bootcampId, userId]);

  const groupedLessons = useMemo(
    () => groupReflectionsByLesson(rows, lessonsById),
    [rows, lessonsById],
  );

  const totalReflections = useMemo(
    () => groupedLessons.reduce((sum, group) => sum + group.responseCount, 0),
    [groupedLessons],
  );

  if (loading) {
    return <div className="user-reflections-state">Loading reflections…</div>;
  }

  if (error) {
    return <div className="modern-error">{error}</div>;
  }

  if (groupedLessons.length === 0) {
    return <div className="user-reflections-state">No reflections written yet.</div>;
  }

  return (
    <div className="user-reflections-overview">
      <p className="user-reflections-summary">
        {totalReflections} reflection{totalReflections !== 1 ? 's' : ''} across{' '}
        {groupedLessons.length} checkpoint{groupedLessons.length !== 1 ? 's' : ''}
      </p>

      {groupedLessons.map((lessonGroup) => {
        const lessonKey = lessonGroup.lesson?.lesson_id || lessonGroup.position;
        const moduleTitle = lessonGroup.lesson?.title || `Module ${lessonGroup.position}`;
        const modulePosition = lessonGroup.lesson?.position || lessonGroup.position;

        return (
          <section key={lessonKey} className="user-reflections-module">
            <header className="user-reflections-module-header">
              <span className="user-reflections-module-badge">Checkpoint {modulePosition}</span>
              <div className="user-reflections-module-heading">
                <h3 className="user-reflections-module-title">{moduleTitle}</h3>
                <p className="user-reflections-module-meta">
                  {lessonGroup.responseCount} reflection{lessonGroup.responseCount !== 1 ? 's' : ''}
                </p>
              </div>
            </header>

            <div className="user-reflections-module-body">
              {lessonGroup.questions.map((question, questionIndex) => (
                <article key={question.questionId} className="user-reflections-question-card">
                  <div className="user-reflections-question-header">
                    <span className="user-reflections-question-index">
                      Question {questionIndex + 1}
                    </span>
                    <span className="user-reflections-question-label">Reflection question</span>
                  </div>
                  <p className="user-reflections-question-prompt">{question.prompt}</p>

                  <div className="user-reflections-responses">
                    {question.responses.map((reflection) => (
                      <UserReflectionResponse key={reflection.id} reflection={reflection} />
                    ))}
                  </div>
                </article>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

export default BootcampUserReflectionsOverview;
